// src/pages/AdminPayments.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { db } from '../firebase/config';
import { collection, query, orderBy, onSnapshot, doc, updateDoc } from 'firebase/firestore';

function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [imageDialog, setImageDialog] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'payments'), orderBy('timestamp', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const paymentData = [];
      snapshot.forEach((doc) => {
        paymentData.push({ id: doc.id, ...doc.data() });
      });
      setPayments(paymentData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleVerifyPayment = async (paymentId, status) => {
    try {
      await updateDoc(doc(db, 'payments', paymentId), {
        status: status,
        verifiedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating payment status:', error);
      alert('Failed to update payment status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'verified':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          Payment Verifications
        </Typography>

        <TableContainer component={Paper} sx={{ mt: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>UTR Number</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Screenshot</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment) => (
                <TableRow key={payment.id}>
                  <TableCell>
                    {payment.timestamp?.toDate().toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{payment.customerName}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {payment.customerPhone}
                    </Typography>
                  </TableCell>
                  <TableCell>₹{payment.amount}</TableCell>
                  <TableCell>{payment.utrNumber}</TableCell>
                  <TableCell>
                    <Chip 
                      label={payment.status} 
                      color={getStatusColor(payment.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {payment.paymentProof && (
                      <IconButton
                        onClick={() => {
                          setSelectedPayment(payment);
                          setImageDialog(true);
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>
                    {payment.status === 'pending' && (
                      <Box>
                        <IconButton
                          color="success"
                          onClick={() => handleVerifyPayment(payment.id, 'verified')}
                        >
                          <CheckIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleVerifyPayment(payment.id, 'rejected')}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {loading && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography>Loading payments...</Typography>
          </Box>
        )}

        {!loading && payments.length === 0 && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography>No payments found</Typography>
          </Box>
        )}

        {/* Image Preview Dialog */}
        <Dialog 
          open={imageDialog} 
          onClose={() => setImageDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              Payment Screenshot
              <IconButton onClick={() => setImageDialog(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedPayment?.paymentProof && (
              <Box
                component="img"
                src={selectedPayment.paymentProof}
                alt="Payment Screenshot"
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '80vh',
                  objectFit: 'contain'
                }}
              />
            )}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>UTR Number:</strong> {selectedPayment?.utrNumber}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Amount:</strong> ₹{selectedPayment?.amount}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Customer:</strong> {selectedPayment?.customerName}
              </Typography>
              {selectedPayment?.status === 'pending' && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => {
                      handleVerifyPayment(selectedPayment.id, 'verified');
                      setImageDialog(false);
                    }}
                    sx={{ mr: 1 }}
                  >
                    Verify Payment
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => {
                      handleVerifyPayment(selectedPayment.id, 'rejected');
                      setImageDialog(false);
                    }}
                  >
                    Reject Payment
                  </Button>
                </Box>
              )}
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
    </Container>
  );
}

export default AdminPayments;